/**
 * Zustand store for global retrieval refresh progress state.
 *
 * Owned by RetrievalProgressRefreshManager (headless component mounted outside Router).
 * The manager registers connect/disconnect functions here on mount, so any component
 * can trigger or read SSE progress without holding the subscription itself.
 *
 * Used by:
 * - RetrievalProgressRefreshManager: registers fns, writes progress
 * - FileExplorer: calls connect() after startRetrieval
 * - Any component reading live progress (SyncStatusBar via FileExplorer, etc.)
 */

import { create } from 'zustand';
import {
  getActiveOrgId,
  isActiveOrg,
  registerOrgScopedState,
  type OrgId,
} from '@/lib/org-scope';
import { devtools } from 'zustand/middleware';
import type { RefreshPhaseDetails } from '@/dataProvider/retrievals';

// ============================================================================
// Types
// ============================================================================

export interface RetrievalProgressState {
  retrievalId: string | null;
  status: string | null;
  message: string | null;
  progressPercentage: number;
  currentPhase: string | null;
  totalItems: number;
  processedItems: number;
  retrievalType: 'full_org_refresh' | 'metadata_type' | null;
  phaseDetails: RefreshPhaseDetails | null;
  isConnected: boolean;
  isComplete: boolean;
  connectionError: string | null;
  /** Set when refresh completes; used as cache-buster so file tree remounts and refetches folder contents */
  lastRefreshCompletedAt: number | null;
  /** Optional context passed when connecting (e.g. metadataType for METADATA_TYPE refresh) for activity display */
  connectMetadataType: string | null;
}

export interface RetrievalProgressSetProgress extends Partial<RetrievalProgressState> {}

/** A refresh whose stream was torn down by an org switch, still running server-side. */
export interface ParkedRetrieval {
  retrievalId: string;
  /** Set for a METADATA_TYPE refresh, so the resumed activity keeps its label. */
  metadataType: string | null;
}

interface RetrievalProgressStoreState extends RetrievalProgressState {
  /**
   * The organization whose retrieval the open stream describes, captured at
   * {@link connect} time. Bookkeeping only — nothing renders it. Every write
   * that could make the UI look busy is checked against it, so a frame that
   * arrives from Org A's refresh while Org B is on screen is dropped instead of
   * relighting Org B's sync bar.
   */
  streamOrgId: OrgId | null;

  /**
   * The metadata type of the open stream's refresh, if it has one. Unlike
   * {@link connectMetadataType} — which the manager clears once it has read it —
   * this survives for the life of the connection, so a refresh parked by an org
   * switch can be resumed with its label intact.
   */
  streamMetadataType: string | null;

  /**
   * Refreshes whose streams were torn down by an org switch, keyed by the
   * organization that owns them. Not rendered: the manager takes the entry when
   * that organization comes back and reconnects to it.
   */
  parkedByOrg: Map<string, ParkedRetrieval>;

  // Functions registered by RetrievalProgressRefreshManager on mount
  _connectFn: ((retrievalId: string) => void) | null;
  _disconnectFn: (() => void) | null;

  // Actions — called by manager on mount
  setConnectFn: (fn: (retrievalId: string) => void) => void;
  setDisconnectFn: (fn: () => void) => void;

  // Actions — called by consumers (e.g. FileExplorer)
  connect: (retrievalId: string, options?: { metadataType?: string }) => void;
  disconnect: () => void;
  clearConnectMetadataType: () => void;

  // Actions — called by manager to sync SSE state
  setProgress: (data: RetrievalProgressSetProgress) => void;
  /** Call when refresh completes so file tree can remount and clear its cache */
  setLastRefreshCompletedAt: (at: number) => void;
  reset: () => void;

  /**
   * Take the refresh parked for `orgId`, if any, and forget it. Called by the
   * manager on the way into an organization: a non-null result is a refresh that
   * was still running when the user last left, and should be reconnected to.
   */
  takeParkedRetrieval: (orgId: OrgId | null) => ParkedRetrieval | null;

  /**
   * Stop following the previous organization's refresh: park it so returning can
   * pick it up, tear the stream down, and clear the progress it published —
   * while keeping `streamOrgId` pointed at that organization so any frame still
   * in flight stays barred. Org-scope only.
   */
  _stopForOrgChange: (previousOrgId: OrgId | null) => void;
}

// ============================================================================
// Initial progress state
// ============================================================================

const initialProgressState: RetrievalProgressState = {
  retrievalId: null,
  status: null,
  message: null,
  progressPercentage: 0,
  currentPhase: null,
  totalItems: 0,
  processedItems: 0,
  retrievalType: null,
  phaseDetails: null,
  isConnected: false,
  isComplete: false,
  connectionError: null,
  lastRefreshCompletedAt: null,
  connectMetadataType: null,
};

// ============================================================================
// Store
// ============================================================================

/**
 * True when the open stream belongs to the organization on screen.
 *
 * A `null` owner means no connection was opened through this store (nothing has
 * streamed yet), so there is nothing to bar; barring it would silently freeze a
 * legitimate progress bar.
 */
function isStreamOnScreen(streamOrgId: OrgId | null): boolean {
  return streamOrgId === null || isActiveOrg(streamOrgId);
}

export const useRetrievalProgressStore = create<RetrievalProgressStoreState>()(
  devtools(
    (set, get) => ({
      ...initialProgressState,
      streamOrgId: null,
      streamMetadataType: null,
      parkedByOrg: new Map(),
      _connectFn: null,
      _disconnectFn: null,

      setConnectFn: (fn) => set({ _connectFn: fn }),
      setDisconnectFn: (fn) => set({ _disconnectFn: fn }),

      connect: (retrievalId, options) => {
        const fn = get()._connectFn;
        if (fn) {
          set({
            retrievalId,
            connectMetadataType: options?.metadataType ?? null,
            streamMetadataType: options?.metadataType ?? null,
            // The organization on screen right now owns this refresh, and owns
            // every frame the stream will deliver for it.
            streamOrgId: getActiveOrgId(),
          });
          fn(retrievalId);
        } else {
          console.warn('[retrievalProgressStore] connect called before manager mounted');
        }
      },

      clearConnectMetadataType: () => set({ connectMetadataType: null }),

      disconnect: () => {
        const fn = get()._disconnectFn;
        if (fn) {
          fn();
        }
      },

      setProgress: (data) => {
        // A frame belonging to an organization the user has left must not be
        // published: this is the write that used to relight the sync bar in Org B
        // a second or two after switching away from a syncing Org A.
        if (!isStreamOnScreen(get().streamOrgId)) return;
        set((state) => ({ ...state, ...data }));
      },

      setLastRefreshCompletedAt: (at) => {
        if (!isStreamOnScreen(get().streamOrgId)) return;
        set({ lastRefreshCompletedAt: at });
      },

      reset: () =>
        set({
          ...initialProgressState,
          lastRefreshCompletedAt: null,
          streamOrgId: null,
          streamMetadataType: null,
        }),

      takeParkedRetrieval: (orgId) => {
        if (!orgId) return null;
        const parked = get().parkedByOrg.get(orgId);
        if (!parked) return null;
        const remaining = new Map(get().parkedByOrg);
        remaining.delete(orgId);
        set({ parkedByOrg: remaining });
        return parked;
      },

      _stopForOrgChange: (previousOrgId) => {
        const { retrievalId, isComplete, streamMetadataType, parkedByOrg } = get();

        // Park a refresh that is still running: it continues server-side either
        // way, and `selectedOrg`'s retrieval status is a snapshot from the last
        // GetUserData — for a sync started since, it still reads COMPLETED, so
        // returning to this organization would otherwise find nothing to resume.
        const parked = new Map(parkedByOrg);
        if (retrievalId && !isComplete && previousOrgId) {
          parked.set(previousOrgId, { retrievalId, metadataType: streamMetadataType });
        }

        get()._disconnectFn?.();
        set({
          ...initialProgressState,
          lastRefreshCompletedAt: null,
          streamOrgId: previousOrgId,
          streamMetadataType: null,
          parkedByOrg: parked,
        });
      },
    }),
    { name: 'retrieval-progress-store' }
  )
);

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Retrieval progress describes a refresh of ONE organization's metadata, and the
// transport can only describe one at a time: there is a single SSE handle, and
// `connection.ts` stamps `X-Organization-ID` from whatever org is selected when
// it (re)connects. So this state is discarded on a switch rather than parked —
// keeping Org A's stream alive would have it reconnect under Org B's identity,
// and would block Org B from attaching to its own refresh.
//
// Resetting alone was not enough, and that was the bug: the stream stayed open
// and its next frame wrote `isConnected: true` straight back into the store, so
// Org B's sync bar lit up again moments after the switch. The stream is now torn
// down, and `streamOrgId` keeps any frame still in flight out.
//
// Nothing is lost by this: the refresh continues server-side and is parked under
// the organization that owns it, so returning reconnects to it (see
// `takeParkedRetrieval` and effect 5 in RetrievalProgressRefreshManager) rather
// than showing an organization that is mid-sync a blank status bar.
registerOrgScopedState({
  name: 'retrievalProgressStore',
  onOrgChange: (_nextOrgId, previousOrgId) => {
    useRetrievalProgressStore.getState()._stopForOrgChange(previousOrgId);
  },
  onSignOut: () => {
    // Parked refreshes go too: their ids would otherwise be reconnected to under
    // the next user's token.
    const store = useRetrievalProgressStore.getState();
    store.disconnect();
    store.reset();
    useRetrievalProgressStore.setState({ parkedByOrg: new Map() });
  },
});
